import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Server,
  Database,
  Activity,
  HardDrive,
  Cpu,
  Mail,
  Bell,
  Users,
  Package,
  Building2,
  MessageSquare,
  AlertCircle,
  RefreshCw,
  Save,
  Play,
  Trash2,
  CheckCircle2,
  XCircle,
  Globe,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonStats } from '@/components/SkeletonStats';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import type { AlertSettings, SystemMetrics, SystemHealth, UnusedResources, MonitoredUrl, MonitoredDomain, DomainCheckResult } from '@/core/models';

export const Route = createFileRoute('/system')({
  head: () => ({
    meta: [{ title: 'Sistema · Replya Internal Backoffice' }],
  }),
  component: SystemPage,
});

const REFRESH_INTERVAL = 30000;
const BYTES_IN_GB = 1024 * 1024 * 1024;

function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return '-';
  return `${(bytes / BYTES_IN_GB).toFixed(1)} GB`;
}

function statusColor(state: 'ok' | 'warning' | 'critical' | string | undefined): string {
  if (state === 'critical') return 'text-destructive';
  if (state === 'warning') return 'text-amber-500';
  return 'text-emerald-500';
}

function metricState(value: number | undefined, threshold: number): 'ok' | 'warning' | 'critical' {
  if (value === undefined || value === null) return 'ok';
  if (threshold > 0 && value >= threshold) return 'critical';
  if (threshold > 0 && value >= threshold * 0.8) return 'warning';
  return 'ok';
}

function SystemPage() {
  const [activeTab, setActiveTab] = useState('overview');

  const healthQuery = useQuery({
    queryKey: ['systemHealth'],
    queryFn: () => apiService.getSystemHealth(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const metricsQuery = useQuery({
    queryKey: ['systemMetrics'],
    queryFn: () => apiService.getSystemMetrics(),
    refetchInterval: REFRESH_INTERVAL,
  });

  const unusedQuery = useQuery<UnusedResources, Error>({
    queryKey: ['systemUnusedResources', 30],
    queryFn: () => apiService.getUnusedResources(30),
    enabled: activeTab === 'unused',
  });

  const settingsQuery = useQuery<AlertSettings, Error>({
    queryKey: ['systemAlertSettings'],
    queryFn: () => apiService.getAlertSettings(),
    enabled: activeTab === 'alerts',
  });

  const domainsQuery = useQuery<DomainCheckResult[], Error>({
    queryKey: ['systemDomains'],
    queryFn: () => apiService.getSystemDomains(),
    enabled: activeTab === 'domains',
    refetchInterval: activeTab === 'domains' ? REFRESH_INTERVAL : false,
  });

  const checkMutation = useMutation({
    mutationFn: () => apiService.checkAlerts(),
    onSuccess: (data) => {
      if (data.checked) {
        toast.success(`Check completato${data.emailSent ? ' · email inviata' : ''}`);
      } else {
        toast.info('Alert disabilitati');
      }
    },
    onError: (err: Error) => toast.error(`Check fallito: ${err.message}`),
  });

  const shownToasts = useRef<Set<string>>(new Set());

  useEffect(() => {
    const health = healthQuery.data;
    if (!health) return;
    if (health.status !== 'ok' && !shownToasts.current.has('health')) {
      shownToasts.current.add('health');
      toast.error(`Sistema in stato ${health.status}: DB ${health.db}, Redis ${health.redis}`, { duration: 10000 });
    }
  }, [healthQuery.data]);

  useEffect(() => {
    const metrics = metricsQuery.data;
    if (!metrics) return;
    const checks: { key: keyof SystemMetrics; label: string; value?: number; threshold: number }[] = [
      { key: 'cpu', label: 'CPU', value: metrics.cpu?.usagePercent, threshold: 85 },
      { key: 'memory', label: 'Memoria', value: metrics.memory?.percent, threshold: 85 },
      { key: 'disk', label: 'Disco', value: metrics.disk?.percent, threshold: 90 },
    ];
    checks.forEach(({ key, label, value, threshold }) => {
      const state = metricState(value, threshold);
      const toastKey = `metric-${key}`;
      if (state === 'critical' && !shownToasts.current.has(toastKey)) {
        shownToasts.current.add(toastKey);
        toast.error(`${label} al ${value}% — soglia critica ${threshold}%`, { duration: 8000 });
      }
    });
  }, [metricsQuery.data]);

  const isLoading = healthQuery.isLoading || metricsQuery.isLoading;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Sistema"
        subtitle="Monitoraggio stato servizi, VPS e alert"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            healthQuery.refetch();
            metricsQuery.refetch();
          }}
          disabled={isLoading}
          className="rounded-xl"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </PageHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-xl bg-surface-2 flex-wrap h-auto">
          <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Panoramica
          </TabsTrigger>
          <TabsTrigger value="domains" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Domini
          </TabsTrigger>
          <TabsTrigger value="unused" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Risorse inutilizzate
          </TabsTrigger>
          <TabsTrigger value="alerts" className="rounded-lg data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            Alert
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <OverviewTab health={healthQuery.data} metrics={metricsQuery.data} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="domains" className="space-y-4 mt-4">
          <DomainsTab query={domainsQuery} />
        </TabsContent>

        <TabsContent value="unused" className="space-y-4 mt-4">
          <UnusedResourcesTab query={unusedQuery} />
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4 mt-4">
          <AlertsTab
            query={settingsQuery}
            onCheck={() => checkMutation.mutate()}
            isChecking={checkMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({
  health,
  metrics,
  isLoading,
}: {
  health?: SystemHealth;
  metrics?: SystemMetrics;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <>
        <SkeletonStats count={3} />
        <SkeletonStats count={4} />
      </>
    );
  }

  return (
    <>
      <section className="grid gap-4 md:grid-cols-3">
        <ServiceCard
          title="Backend"
          icon={Server}
          status={health?.status === 'ok' ? 'ok' : 'critical'}
          detail={health ? `Uptime ${Math.floor(health.uptime / 60)}m` : undefined}
        />
        <ServiceCard
          title="MongoDB"
          icon={Database}
          status={health?.db === 'connected' ? 'ok' : 'critical'}
          detail={health?.db}
        />
        <ServiceCard
          title="Redis"
          icon={Activity}
          status={health?.redis === 'ok' ? 'ok' : 'critical'}
          detail={health?.redis}
        />
      </section>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="CPU"
          icon={Cpu}
          value={metrics?.cpu?.usagePercent}
          threshold={85}
          format={(v) => `${v}%`}
        />
        <MetricCard
          label="Memoria"
          icon={Activity}
          value={metrics?.memory?.percent}
          threshold={85}
          format={(v) => `${v}%`}
          detail={metrics?.memory ? `${formatBytes(metrics.memory.used)} / ${formatBytes(metrics.memory.total)}` : undefined}
        />
        <MetricCard
          label="Disco"
          icon={HardDrive}
          value={metrics?.disk?.percent}
          threshold={90}
          format={(v) => `${v}%`}
          detail={metrics?.disk ? `${formatBytes(metrics.disk.used)} / ${formatBytes(metrics.disk.total)}` : undefined}
        />
        <MetricCard
          label="Load average"
          icon={Server}
          value={metrics?.loadAvg?.percent}
          threshold={85}
          format={() => (metrics?.loadAvg ? `${metrics.loadAvg.oneMin.toFixed(2)}` : '-')}
          detail={metrics?.loadAvg ? `5m ${metrics.loadAvg.fiveMin.toFixed(2)} · 15m ${metrics.loadAvg.fifteenMin.toFixed(2)}` : undefined}
        />
      </section>
    </>
  );
}

function ServiceCard({
  title,
  icon: Icon,
  status,
  detail,
}: {
  title: string;
  icon: React.ElementType;
  status: 'ok' | 'critical';
  detail?: string;
}) {
  return (
    <Card className="rounded-2xl border bg-surface">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-brand" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          {status === 'ok' ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <span className="font-semibold text-emerald-500">Operativo</span>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-destructive" />
              <span className="font-semibold text-destructive">Problema</span>
            </>
          )}
        </div>
        {detail && <p className="text-sm text-muted-foreground capitalize">{detail}</p>}
      </CardContent>
    </Card>
  );
}

function MetricCard({
  label,
  icon: Icon,
  value,
  threshold,
  format,
  detail,
}: {
  label: string;
  icon: React.ElementType;
  value: number | undefined;
  threshold: number;
  format: (v: number) => string;
  detail?: string;
}) {
  const state = metricState(value, threshold);
  const displayValue = value !== undefined && value !== null ? format(value) : '-';

  return (
    <Card className="rounded-2xl border bg-surface">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <Icon className={`h-4 w-4 ${state === 'critical' ? 'text-destructive' : 'text-brand'}`} />
        </div>
        <p className={`text-3xl font-bold tracking-tight ${statusColor(state)}`}>{displayValue}</p>
        {value !== undefined && value !== null && (
          <Progress value={Math.min(100, value)} className="h-2" />
        )}
        {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
      </CardContent>
    </Card>
  );
}

function UnusedResourcesTab({ query }: { query: UseQueryResult<UnusedResources, Error> }) {
  const { data, isLoading, isError, error, refetch } = query;

  if (isLoading) return <SkeletonStats count={4} />;
  if (isError) return <QueryError error={error as Error} onRetry={refetch} />;
  if (!data) return null;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ResourceCard
        title="Tenant inattivi"
        icon={Building2}
        count={data.inactiveTenants.length}
        detail={`Nessuna richiesta negli ultimi ${data.days} giorni`}
        items={data.inactiveTenants.map((t) => ({ id: t.tenantId, label: t.name || t.tenantId }))}
      />
      <ResourceCard
        title="Prodotti non indicizzati"
        icon={Package}
        count={data.productsNotIndexed.reduce((sum, p) => sum + p.count, 0)}
        detail="Assenti dall'indice attivo del tenant (Qdrant per il motore v2)"
        items={data.productsNotIndexed.map((p) => ({
          id: p.tenantId,
          label: `${p.name || p.tenantId}: ${p.count}${p.indexSource ? ` (${p.indexSource})` : ''}`,
        }))}
      />
      <ResourceCard
        title="Utenti disabilitati"
        icon={Users}
        count={data.disabledUsers.length}
        detail="Account Firebase bloccati"
        items={data.disabledUsers.map((u) => ({ id: u.firebaseId, label: u.email || u.firebaseId }))}
      />
      <ResourceCard
        title="Conversazioni stale"
        icon={MessageSquare}
        count={data.staleConversations}
        detail={`Senza attività negli ultimi ${data.days} giorni`}
        items={[]}
      />
    </div>
  );
}

function ResourceCard({
  title,
  icon: Icon,
  count,
  detail,
  items,
}: {
  title: string;
  icon: React.ElementType;
  count: number;
  detail: string;
  items: { id: string; label: string }[];
}) {
  return (
    <Card className="rounded-2xl border bg-surface">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-brand" />
          {title}
        </CardTitle>
        <CardDescription>{detail}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-3xl font-bold tracking-tight">{count}</p>
        {items.length > 0 && (
          <div className="max-h-40 overflow-auto space-y-1">
            {items.map((item) => (
              <div key={item.id} className="rounded-lg bg-surface-2 px-3 py-2 text-sm">
                {item.label}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertsTab({
  query,
  onCheck,
  isChecking,
}: {
  query: UseQueryResult<AlertSettings, Error>;
  onCheck: () => void;
  isChecking: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = query;
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [urls, setUrls] = useState<MonitoredUrl[]>([]);
  const [newUrl, setNewUrl] = useState('');
  const [newUrlLabel, setNewUrlLabel] = useState('');
  const [newUrlExpectedStatus, setNewUrlExpectedStatus] = useState(200);
  const [settings, setSettings] = useState<Partial<AlertSettings>>({});

  useEffect(() => {
    if (data) {
      setRecipients(data.emailRecipients || []);
      setUrls(data.monitoredUrls || []);
      setSettings({
        enabled: data.enabled,
        emailEnabled: data.emailEnabled,
        thresholds: data.thresholds,
      });
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (body: Partial<AlertSettings>) => apiService.updateAlertSettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemAlertSettings'] });
      toast.success('Impostazioni alert salvate');
    },
    onError: (err: Error) => toast.error(`Salvataggio fallito: ${err.message}`),
  });

  const handleSave = () => {
    updateMutation.mutate({
      ...settings,
      emailRecipients: recipients,
      monitoredUrls: urls,
    });
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Indirizzo email non valido');
      return;
    }
    if (recipients.includes(email)) {
      toast.error('Email già presente');
      return;
    }
    setRecipients((prev) => [...prev, email]);
    setNewRecipient('');
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((e) => e !== email));
  };

  const handleAddUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      toast.error('URL non valido');
      return;
    }
    if (urls.some((u) => u.url === url)) {
      toast.error('URL già presente');
      return;
    }
    setUrls((prev) => [
      ...prev,
      {
        url,
        label: newUrlLabel.trim() || url,
        method: 'HEAD',
        expectedStatus: newUrlExpectedStatus,
        timeoutMs: 10000,
      },
    ]);
    setNewUrl('');
    setNewUrlLabel('');
    setNewUrlExpectedStatus(200);
  };

  const handleRemoveUrl = (url: string) => {
    setUrls((prev) => prev.filter((u) => u.url !== url));
  };

  const updateThreshold = (key: keyof AlertSettings['thresholds'], value: string) => {
    const num = parseInt(value, 10);
    setSettings((prev) => ({
      ...prev,
      thresholds: {
        ...(prev.thresholds || { cpu: 85, memory: 85, disk: 90, loadAvg: 0, errorRate: 10 }),
        [key]: Number.isNaN(num) ? 0 : num,
      },
    }));
  };

  if (isLoading) return <SkeletonStats count={3} />;
  if (isError) return <QueryError error={error as Error} onRetry={refetch} />;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="rounded-2xl border bg-surface md:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4 text-brand" />
            Configurazione alert
          </CardTitle>
          <CardDescription>
            Gli alert sono attivi di default e inviano email quando una metrica supera la soglia configurata.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <Switch
                id="alerts-enabled"
                checked={settings.enabled ?? true}
                onCheckedChange={(checked: boolean) => setSettings((s) => ({ ...s, enabled: checked }))}
              />
              <Label htmlFor="alerts-enabled" className="text-sm">Abilita alert</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="email-enabled"
                checked={settings.emailEnabled ?? true}
                onCheckedChange={(checked: boolean) => setSettings((s) => ({ ...s, emailEnabled: checked }))}
              />
              <Label htmlFor="email-enabled" className="text-sm">Invia email</Label>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['cpu', 'CPU (%)'],
                ['memory', 'Memoria (%)'],
                ['disk', 'Disco (%)'],
                ['loadAvg', 'Load avg (%)'],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={settings.thresholds?.[key] ?? 0}
                  onChange={(e) => updateThreshold(key, e.target.value)}
                  className="rounded-xl bg-surface-2 border-border h-9"
                />
                <p className="text-xs text-muted-foreground">0 = disabilitato</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" /> Destinatari email
            </Label>
            <div className="flex flex-wrap gap-2">
              {recipients.map((email) => (
                <Badge key={email} variant="secondary" className="gap-1 px-2 py-1">
                  {email}
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(email)}
                    className="ml-1 rounded-full hover:bg-surface-3"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="admin@example.com"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRecipient();
                  }
                }}
                className="rounded-xl bg-surface-2 border-border h-9 sm:w-80"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddRecipient} className="rounded-xl h-9">
                Aggiungi
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Server className="h-4 w-4" /> URL monitorati
            </Label>
            <p className="text-xs text-muted-foreground">
              Riceverai un alert se uno di questi URL non risponde con lo status atteso.
            </p>
            <div className="flex flex-wrap gap-2">
              {urls.map((u) => (
                <Badge key={u.url} variant="secondary" className="gap-1 px-2 py-1">
                  {u.label || u.url} → {u.expectedStatus}
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(u.url)}
                    className="ml-1 rounded-full hover:bg-surface-3"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="https://example.com/api/health"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddUrl();
                  }
                }}
                className="rounded-xl bg-surface-2 border-border h-9 sm:flex-1"
              />
              <Input
                placeholder="Label (opzionale)"
                value={newUrlLabel}
                onChange={(e) => setNewUrlLabel(e.target.value)}
                className="rounded-xl bg-surface-2 border-border h-9 sm:w-48"
              />
              <Input
                type="number"
                min={100}
                max={599}
                value={newUrlExpectedStatus}
                onChange={(e) => setNewUrlExpectedStatus(Number(e.target.value) || 200)}
                className="rounded-xl bg-surface-2 border-border h-9 w-24"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddUrl} className="rounded-xl h-9">
                Aggiungi
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9"
            >
              <Save className="mr-2 h-4 w-4" />
              Salva
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCheck}
              disabled={isChecking}
              className="rounded-xl h-9"
            >
              <Play className="mr-2 h-4 w-4" />
              Controlla ora
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DomainsTab({ query }: { query: UseQueryResult<DomainCheckResult[], Error> }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = query;
  const [domains, setDomains] = useState<MonitoredDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    apiService.getAlertSettings()
      .then((settings) => setDomains(settings.monitoredDomains || []))
      .catch(() => {});
  }, []);

  const updateMutation = useMutation({
    mutationFn: (body: MonitoredDomain[]) => apiService.updateSystemDomains(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemDomains'] });
      toast.success('Domini aggiornati');
    },
    onError: (err: Error) => toast.error(`Salvataggio fallito: ${err.message}`),
  });

  const handleAdd = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    if (!/^[a-z0-9][a-z0-9-]{0,61}\.[a-z0-9][a-z0-9-]{0,61}(\.[a-z0-9][a-z0-9-]{0,61})*$/i.test(domain)) {
      toast.error('Dominio non valido');
      return;
    }
    if (domains.some((d) => d.domain === domain)) {
      toast.error('Dominio già presente');
      return;
    }
    setDomains((prev) => [...prev, { domain, label: newLabel.trim() || domain }]);
    setNewDomain('');
    setNewLabel('');
  };

  const handleRemove = (domain: string) => {
    setDomains((prev) => prev.filter((d) => d.domain !== domain));
  };

  const handleSave = () => {
    updateMutation.mutate(domains);
  };

  if (isLoading) return <SkeletonStats count={3} />;
  if (isError) return <QueryError error={error as Error} onRetry={refetch} />;

  const checks = data || [];
  const checksByDomain = new Map(checks.map((c) => [c.domain, c]));

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4 text-brand" />
            Domini controllati
          </CardTitle>
          <CardDescription>
            Stato DNS, HTTPS e certificato SSL dei domini Replya. I domini vengono controllati automaticamente ogni 30 secondi.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            {domains.map((d) => {
              const check = checksByDomain.get(d.domain);
              return (
                <div key={d.domain} className="flex items-start justify-between rounded-xl bg-surface-2 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{d.label || d.domain}</span>
                      {check?.ok ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{d.domain}</p>
                    {check && (
                      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                        <span>DNS: {check.dnsResolved ? 'ok' : 'fallito'}</span>
                        {check.statusCode !== undefined && <span>HTTP: {check.statusCode}</span>}
                        {check.responseTimeMs !== undefined && <span>{check.responseTimeMs}ms</span>}
                        {check.certDaysRemaining !== undefined && (
                          <span>SSL: {check.certDaysRemaining} gg</span>
                        )}
                        {check.error && <span className="text-destructive">{check.error}</span>}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(d.domain)}
                    className="ml-2 rounded-full hover:bg-surface-3 p-1"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="es. monitor.replya.app"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              className="rounded-xl bg-surface-2 border-border h-9 sm:flex-1"
            />
            <Input
              placeholder="Label (opzionale)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="rounded-xl bg-surface-2 border-border h-9 sm:w-48"
            />
            <Button type="button" variant="outline" size="sm" onClick={handleAdd} className="rounded-xl h-9">
              Aggiungi
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9"
            >
              <Save className="mr-2 h-4 w-4" />
              Salva
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="rounded-xl h-9"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QueryError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4 text-sm text-destructive flex items-center gap-3">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold">Errore durante il caricamento</p>
        <p className="text-destructive/80 break-words">{error.message || 'Riprova tra qualche istante.'}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry} className="rounded-xl shrink-0">
        Riprova
      </Button>
    </div>
  );
}
